import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { type ChatTarget } from '@mint-lib/ai-chat';
import { Suggestions } from '@mint-sdk/api-recommendations';

import { DRAWER_WIDTH } from './constants/common.js';

export type AiSidebarContextProps = {
  isAiSidebarOpen: boolean;
  sidebarWidth: number;
  currentChat: string | null;
  selectedSuggestion: Suggestions.SuggestionResponseType | null;
  setIsAiSidebarOpen: Dispatch<SetStateAction<boolean>>;
  setCurrentChat: Dispatch<SetStateAction<string | null>>;
  setSelectedSuggestion: Dispatch<
    SetStateAction<Suggestions.SuggestionResponseType | null>
  >;
  target?: ChatTarget;
};

type AiSidebarProviderProps = {
  children: ReactNode;
  shouldMoveSidebar?: boolean;
  sidebarWidth?: number;
  target?: ChatTarget;
};

const AiSidebarContext = createContext<AiSidebarContextProps>({
  sidebarWidth: DRAWER_WIDTH,
  isAiSidebarOpen: false,
  currentChat: null,
  setCurrentChat: () => {},
  selectedSuggestion: null,
  setIsAiSidebarOpen: () => {},
  setSelectedSuggestion: () => {},
});

export const AiSidebarProvider = ({
  children,
  sidebarWidth,
  shouldMoveSidebar = true,
  target,
}: AiSidebarProviderProps) => {
  const [isAiSidebarOpen, setIsAiSidebarOpen] = useState<boolean>(false);
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<Suggestions.SuggestionResponseType | null>(null);
  const [currentChat, setCurrentChat] = useState<string | null>(null);

  const contextValue = useMemo(
    () => ({
      isAiSidebarOpen,
      sidebarWidth: sidebarWidth ?? DRAWER_WIDTH,
      setIsAiSidebarOpen,
      selectedSuggestion,
      setSelectedSuggestion,
      currentChat,
      setCurrentChat,
      target,
    }),
    [
      isAiSidebarOpen,
      sidebarWidth,
      setIsAiSidebarOpen,
      selectedSuggestion,
      setSelectedSuggestion,
      currentChat,
      setCurrentChat,
      target,
    ],
  );

  const SidebarProvider = AiSidebarContext.Provider;

  return (
    <SidebarProvider value={contextValue}>
      <div className="w-full" aria-label="ai sidebar">
        {children}
      </div>
    </SidebarProvider>
  );
};
export const useAiSidebarContext = () => useContext(AiSidebarContext);
